/* eslint-disable no-unused-vars */
/* eslint-disable class-methods-use-this */
import moment, { Moment } from 'moment';
import { Node, NodeSpec } from 'prosemirror-model';
import { Transaction } from 'prosemirror-state';
import {
  Decoration, DecorationSet, EditorView, NodeView,
} from 'prosemirror-view';
import { uuid4 } from '@sentry/utils';
import { dbMaybeListenToTask } from '../../../../../database/firebaseTasksAPI';
import TaskAPI from '../../../../../external/TaskAPI/TaskAPI';
import { dateToSDateObject } from '../../../../../utils/dateUtils/date';
import {
  AssigneeV2, DatabaseAssignee, TaskItem, TaskStatus,
} from '../../../../types/types';
import { userToColour } from '../../hooks/use-prosemirror-yjs';
import taskAdapter from './task-adapter';
// interface TaskSpecAttrs {
//   status: boolean;
//   due: number;
//   name: string;
//   userjson: string;
// }

function getTextWidth(text: string, font: string): number {
  // re-use canvas object for better performance
  // @ts-ignore
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

const TaskSpec: NodeSpec = {
  attrs: {
    status: {
      default: 'todo',
    },
    due: {
      default: '',
    },
    name: {
      default: '',
    },
    userjson: {
      default: '{}',
    },
    taskid: {
      default: '',
    },
    deleted: {
      default: '',
    },
    id: {
      default: '',
    },
    locked: {
      default: 'no',
    },
  },
  draggable: true,
  inline: true,
  defining: true,
  isolating: true,
  content: 'text*',
  atom: false,
  group: 'inline',
  marks: '',
  toDOM: (node) => [
    'span',
    {
      class: 'task-wrapper',
      contenteditable: 'true',
      'data-user': node.attrs.user,
      'data-status': node.attrs.status,
      'data-due': node.attrs.due,
      'data-named': node.attrs.name,
      'data-taskid': node.attrs.taskid,
      'data-id': node.attrs.id,
      'data-locked': node.attrs.locked,
    },
    0,
  ],
  parseDOM: [{
    tag: 'div.task-wrapper',
    getAttrs: (dom) => ({
      user: (dom as Element).getAttribute('data-user') || '',
      status: (dom as Element).getAttribute('data-status') || 'todo',
      due: new Date((dom as Element).getAttribute('data-due') || 'Now'),
      name: (dom as Element).getAttribute('data-name') || '',
      userjson: (dom as Element).getAttribute('data-userjson') || '{}',
      taskid: (dom as Element).getAttribute('data-taskid') || '',
      id: (dom as Element).getAttribute('data-id'),
      locked: (dom as Element).getAttribute('data-locked') || 'no',
    }),
  }],
};
class TaskView implements NodeView {
  public node: Node;

  public outerView: EditorView;

  public getPos: () => number;

  public dom: HTMLElement;

  public contentDOM: HTMLElement | undefined;

  get user(): AssigneeV2 {
    return JSON.parse(this.node.attrs.userjson);
  }

  // public updateState(
  //   state: Partial<TaskSpecAttrs>, getPos: () => number, disptach = true,
  // ): Transaction<any> {
  //   const { dispatch } = this.outerView;
  //   const { tr } = this.outerView.state;

  //   tr.setNodeMarkup(
  //     getPos(),
  //     undefined,
  //     {
  //       ...this.node.attrs,
  //       ...state,
  //     },
  //   );

  //   if (disptach) {
  //     dispatch(tr);
  //   }

  //   return tr;
  // }

  constructor(node: Node, view: EditorView, getPos: () => number) {
    this.node = node;
    this.outerView = view;
    this.getPos = getPos;

    // create a div to hold some text
    const el = document.createElement('div');
    el.classList.add('task-wrapper');
    el.setAttribute('data-user', node.attrs.user);
    el.setAttribute('data-status', node.attrs.status);
    el.setAttribute('data-due', node.attrs.due);
    el.setAttribute('data-name', node.attrs.name);
    el.setAttribute('data-userjson', node.attrs.userjson);
    el.setAttribute('data-taskid', node.attrs.taskid);
    el.setAttribute('data-deleted', node.attrs.deleted);
    el.setAttribute('data-id', node.attrs.id);
    el.setAttribute('data-locked', node.attrs.locked);

    if (node.attrs.locked === 'yes') {
      el.classList.add('locked-prosemirror-task');
    }

    // Checkbox Section
    let checkbox: HTMLSpanElement;
    if (node.attrs.deleted !== 'yes') {
      checkbox = document.createElement('span');
      checkbox.classList.add('task-checkbox');
      checkbox.contentEditable = 'false';
      el.appendChild(checkbox);
    }

    // User Section
    let user: HTMLSpanElement;
    if (node.attrs.deleted !== 'yes') {
      user = document.createElement('span');
      user.textContent = this.user?.data?.name || this.user?.data?.email || 'Error';
      user.classList.add('pm-task-user');
      user.classList.add(`pyc-${userToColour(this.user?.userId || 0)}-body`);
      user.contentEditable = 'false';
      el.appendChild(user);
    }

    // Task Name Section
    const name = document.createElement('div');
    name.textContent = node.attrs.name;
    name.classList.add('pm-task-name');
    name.setAttribute('placeholder', 'Start typing task name...');
    if (node.attrs.deleted !== 'yes') {
      name.style.textIndent = `${getTextWidth(
        this.user?.data?.name || this.user?.data?.email || 'Error',
        '500 14px Inter',
      ) + 60}px`;
    }
    if (node.attrs.locked === 'yes') {
      name.contentEditable = 'false';
    }
    el.appendChild(name);

    // Due Date Section
    let calendarTrigger: HTMLElement;
    if (!node.attrs.due && node.attrs.deleted !== 'yes') {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 15.75 16.5');
      svg.setAttribute('width', '15.75');
      svg.setAttribute('height', '16.5');
      svg.setAttribute('fill', '#6f7c9f');
      // svg.setAttribute('style', 'margin-left: 5px;');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm 4.2792968,0 c -0.41421,0 -0.75,0.33579 -0.75,0.75 V 1.0410156 C 1.4974873,1.579797 0,3.4315027 0,5.6328125 V 11.75 c 0,2.6233 2.12664,4.75 4.75,4.75 h 6.117187 c 2.6233,0 4.75,-2.1267 4.75,-4.75 V 5.6328125 c 0,-2.2013098 -1.49751,-4.0530155 -3.529297,-4.5917969 V 0.75 c 0,-0.41421 -0.3358,-0.75 -0.75,-0.75 -0.4142,0 -0.75,0.33579 -0.75,0.75 V 0.8828125 H 5.0292968 V 0.75 c 0,-0.41421 -0.33578,-0.75 -0.75,-0.75 z m 0.75,2.3828125 H 10.58789 v 1.0136719 c 0,0.41421 0.3358,0.75 0.75,0.75 0.4142,0 0.75,-0.33579 0.75,-0.75 V 2.6230469 c 1.090049,0.442327 1.888032,1.4562964 2.013672,2.671875 H 1.5175781 C 1.6432184,4.0793479 2.4392466,3.0653726 3.5292968,2.6230469 v 0.7734375 c 0,0.41421 0.33579,0.75 0.75,0.75 0.41422,0 0.75,-0.33579 0.75,-0.75 z M 1.4999999,6.7949219 H 14.117187 V 11.75 c 0,1.7949 -1.4551,3.25 -3.25,3.25 H 4.75 C 2.9550701,15 1.5,13.5449 1.5,11.75 Z m 2.8652344,1.8964843 -0.087891,0.00586 c -0.28396,0.03883 -0.5019531,0.2833081 -0.5019531,0.5800784 0,0.3238 0.2607212,0.585937 0.5820312,0.585937 l 0.085937,-0.00391 c 0.28397,-0.0389 0.5019532,-0.285131 0.5019532,-0.582031 0,-0.3237503 -0.2587782,-0.5859378 -0.5800782,-0.5859378 z m 3.4492188,0 -0.087891,0.00586 C 7.4426024,8.7360956 7.2246093,8.9805737 7.2246093,9.277344 c 0,0.3238 0.2613538,0.585937 0.5898438,0.585937 l 0.078125,-0.00391 C 8.1765482,9.820471 8.3945313,9.57424 8.3945313,9.27734 c 0,-0.3237503 -0.2587781,-0.5859378 -0.5800782,-0.5859378 z m 3.4414059,0 -0.08594,0.00586 c -0.2839,0.03883 -0.503906,0.2833081 -0.503906,0.5800784 0,0.3238 0.261344,0.585937 0.589844,0.585937 l 0.07813,-0.00391 c 0.284,-0.0389 0.503906,-0.285131 0.503906,-0.582031 0,-0.3237503 -0.260731,-0.5859378 -0.582031,-0.5859378 z m -6.8906247,3.0371098 -0.087891,0.0059 c -0.28396,0.0388 -0.5019531,0.285231 -0.5019531,0.582031 0,0.3238 0.2607212,0.585938 0.5820312,0.585938 l 0.085937,-0.0059 c 0.28397,-0.0389 0.5019532,-0.283278 0.5019532,-0.580078 0,-0.3238 -0.2587782,-0.58789 -0.5800782,-0.58789 z m 3.4492188,0 -0.087891,0.0059 c -0.2839601,0.0388 -0.5019532,0.285231 -0.5019532,0.582031 0,0.3238 0.2613538,0.585938 0.5898438,0.585938 l 0.078125,-0.0059 c 0.28397,-0.0389 0.5019532,-0.283278 0.5019532,-0.580078 0,-0.3238 -0.2587781,-0.58789 -0.5800782,-0.58789 z m 3.4414059,0 -0.08594,0.0059 c -0.2839,0.0388 -0.503906,0.285231 -0.503906,0.582031 0,0.3238 0.261344,0.585938 0.589844,0.585938 l 0.07813,-0.0059 c 0.284,-0.0389 0.503906,-0.283278 0.503906,-0.580078 0,-0.3238 -0.260731,-0.58789 -0.582031,-0.58789 z');
      svg.appendChild(path);

      // wrap the svg in a flex span which centers it
      const svgWrap = document.createElement('span');
      // svgWrap.setAttribute('style', 'display: flex; align-items: center; cursor: pointer;');
      svgWrap.classList.add('pm-task-calendar');
      svgWrap.appendChild(svg);
      el.appendChild(svgWrap);
      calendarTrigger = svgWrap;
    } else if (node.attrs.deleted !== 'yes') {
      const due = moment(parseInt(node.attrs.due, 10));
      const dueSpan = document.createElement('span');
      dueSpan.textContent = due.format('D MMM');
      dueSpan.classList.add('pm-task-due-date');
      dueSpan.classList.add(`pyc-${userToColour(this.user?.userId || 0)}-body`);
      dueSpan.contentEditable = 'false';
      el.appendChild(dueSpan);
      calendarTrigger = dueSpan;
    } else if (node.attrs.deleted === 'yes') {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '12');
      svg.setAttribute('height', '12');
      svg.setAttribute('fill', '#6f7c9f');
      svg.setAttribute('style', 'margin-top: 4px; margin-right: 4px; float: right; cursor: pointer;');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M 21.509375,8.4459423e-5 A 2.4884597,2.4884597 0 0 0 19.750315,0.72939446 L 12.000004,8.4797048 4.2496925,0.72939446 a 2.4884597,2.4884597 0 0 0 -3.52029609,0 2.4884597,2.4884597 0 0 0 0,3.52030004 L 8.479708,11.999995 0.72939641,19.750315 a 2.4884597,2.4884597 0 0 0 0,3.51812 2.4884597,2.4884597 0 0 0 3.52029609,0 l 7.7503115,-7.75032 7.750311,7.75032 a 2.4884597,2.4884597 0 0 0 3.520296,0 2.4884597,2.4884597 0 0 0 0,-3.51812 L 15.5203,11.999995 23.270611,4.2496945 a 2.4884597,2.4884597 0 0 0 0,-3.52030004 A 2.4884597,2.4884597 0 0 0 21.509375,8.4459423e-5 Z');
      svg.appendChild(path);

      const svgWrap = document.createElement('span');
      svgWrap.classList.add('pm-task-calendar');
      svgWrap.appendChild(svg);
      svgWrap.addEventListener('click', (e) => {
        view.dispatch(
          view.state.tr.deleteRange(getPos(), getPos() + node.content.size + 2),
        );
      });
      el.appendChild(svgWrap);
    }

    const enterMessage = document.createElement('span');
    enterMessage.classList.add('pm-enter-message');
    enterMessage.setAttribute('contenteditable', 'false');
    el.appendChild(enterMessage);

    if (node.attrs.deleted !== 'yes' && node.attrs.locked !== 'yes') {
      checkbox!.addEventListener('mouseup', (e) => {
        taskAdapter.statusMenuShow(
          true,
          node.attrs.status,
          (status) => {
            // If this is a 'resolved' task,
            // we need to modify it in the database.
            if (node.attrs.taskid) {
              try {
                TaskAPI.updateStatus(
                  node.attrs.taskid,
                  status,
                ).then((result) => {
                  if (result === 'resolved') {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(
                        getPos(),
                        node.type,
                        {
                          ...node.attrs,
                          status,
                        },
                      ),
                    );
                  }
                });
              } catch (err) {
                console.debug('Skip update.');
              }
            } else {
              // If the task is not 'resolved' yet,
              // it doesn't make sense to update anything.
              //
              // view.dispatch(
              //   view.state.tr.setNodeMarkup(
              //     getPos(),
              //     node.type,
              //     {
              //       ...node.attrs,
              //       status,
              //     },
              //   ),
              // );
            }
          },
          checkbox.getBoundingClientRect().bottom,
          checkbox.getBoundingClientRect().left - 10,
        );
      });
      calendarTrigger!.addEventListener('mouseup', (e) => {
        taskAdapter.calendarMenuShow(
          true,
          (due: Date | Moment | null) => {
            let parsedDue: string;
            if (due instanceof Date) {
              parsedDue = `${+due}`;
            } else if (!due) {
              parsedDue = '';
            } else {
              parsedDue = `${+due}`;
            }
            // If this is not a task already, and has a name.
            // TODO: If you want to allow empty name tasks, here
            // is the place to edit the code.
            if (!node.attrs.taskid && this.contentDOM?.innerText) {
              const taskName = this.contentDOM.innerText;
              if (!this.user?.data?.email) return;
              TaskAPI.createTaskVariant3(
                taskName,
                taskAdapter.getCurrentUser()!.email,
                {
                  ...taskAdapter.getCurrentMeetingData()!,
                  startDate: dateToSDateObject(new Date()),
                  tags: [],
                },
                this.user?.data?.email,
                due ? dateToSDateObject(new Date(parseInt(parsedDue, 10))) : undefined,
              ).then((taskId) => {
                if (!taskId) {
                  // If task creation failed, keep modifications.
                  // Maybe the user will succeed on the second attempt,
                  // and they expect updated data.
                  view.dispatch(
                    view.state.tr.setNodeMarkup(
                      getPos(),
                      node.type,
                      {
                        ...node.attrs,
                        due: parsedDue,
                      },
                    ),
                  );
                } else {
                  // If the task creation succeeded, update the node
                  // with the latest due date, but also mark it as
                  // a 'resolved' task by putting in the task id.
                  view.dispatch(
                    view.state.tr.setNodeMarkup(
                      getPos(),
                      node.type,
                      {
                        ...node.attrs,
                        taskid: taskId,
                        due: parsedDue,
                        name: taskName,
                      },
                    ),
                  );
                }
              });
            } else if (node.attrs.taskid) {
              TaskAPI.updateDueDate(
                node.attrs.taskid,
                new Date(parseInt(parsedDue, 10)),
              ).then((result) => {
                if (result === 'resolved') {
                  view.dispatch(
                    view.state.tr.setNodeMarkup(
                      getPos(),
                      node.type,
                      {
                        ...node.attrs,
                        due: parsedDue,
                      },
                    ),
                  );
                }
              });
            }
          },
          node.attrs.due ? new Date(parseInt(node.attrs.due, 10)) : null,
          calendarTrigger.getBoundingClientRect().bottom,
          calendarTrigger.getBoundingClientRect().left - 10,
        );
      });
      user!.addEventListener('mouseup', (e) => {
        taskAdapter.assigneeMenuShow(
          true,
          (assignee) => {
            if (!assignee) return;

            // If this is a 'resolved' task,
            // we need to modify it in the database.
            if (node.attrs.taskid) {
              try {
                TaskAPI.updateAssignee(
                  node.attrs.taskid,
                  assignee,
                ).then((result) => {
                  if (result === 'resolved') {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(
                        getPos(),
                        node.type,
                        {
                          ...node.attrs,
                          userjson: JSON.stringify(assignee),
                        },
                      ),
                    );
                  }
                });
              } catch (err) {
                console.debug('Update skipped.');
              }
            } else {
              view.dispatch(
                view.state.tr.setNodeMarkup(
                  getPos(),
                  node.type,
                  {
                    ...node.attrs,
                    userjson: JSON.stringify(assignee),
                  },
                ),
              );
            }
          },
          JSON.parse(node.attrs.userjson),
          user.getBoundingClientRect().bottom,
          user.getBoundingClientRect().left - 10,
        );
      });
    }

    this.dom = el;

    if (!node.attrs.taskid) {
      this.contentDOM = name;
    } else {
      name.innerText = node.attrs.deleted !== 'yes' ? node.attrs.name : 'This task has been deleted.';

      name.addEventListener('click', () => {
        taskAdapter.setTaskModalShow(true, node.attrs.taskid);
      });

      name.style.cursor = 'pointer';

      this.contentDOM = undefined;
      // @ts-ignore
      dbMaybeListenToTask(node.attrs.taskid, (task: TaskItem) => {
        let newAssignee;
        let newDue;
        let newName;
        let newStatus: TaskStatus | undefined;

        if (JSON.stringify(task.assignee) !== node.attrs.userjson) {
          newAssignee = JSON.stringify(task.assignee);
        }

        const taskDate = task.date.dueDate.date.timestamp;
        // if the date has been unset
        if (task.date.dueDate.type === 'noDueDate' && node.attrs.due) {
          newDue = '';
        // else, if the date is not on the same day
        // TODO: If you ever have more fine-grained timestamps here,
        // you should check the hour, minute, etc.
        } else if (!(node.attrs.due && moment(parseInt(node.attrs.due, 10)).isSame(moment(taskDate), 'day'))) {
          newDue = taskDate;
        }

        if (task.data.title !== node.attrs.name) {
          if (node.attrs.deleted !== 'yes') {
            newName = task.data.title;
          } else {
            newName = 'This task has been deleted.';
          }
        }

        if (task.data.status !== node.attrs.status) {
          newStatus = task.data.status;
        }

        if (newAssignee || newDue || newName || newStatus) {
          try {
            view.dispatch(
              view.state.tr.setNodeMarkup(
                getPos(),
                node.type,
                {
                  ...node.attrs,
                  deleted: 'no',
                  ...(newAssignee ? { userjson: newAssignee } : {}),
                  ...(newDue ? { due: newDue } : {}),
                  ...(newName ? { name: newName } : {}),
                  ...(newStatus ? { status: newStatus } : {}),
                },
              ),
            );
          } catch (e) {
            console.debug('Task update skipped.');
          }
        }
      },
      () => {
        try {
          view.dispatch(
            view.state.tr.setNodeMarkup(
              getPos(),
              node.type,
              {
                ...node.attrs,
                deleted: 'yes',
              },
            ),
          );
        } catch (err) {
          console.debug('Task deletion skipped.');
        }
      });
    }
  }

  selectNode() {
    this.dom.classList.add('ProseMirror-selectednode');
  }

  deselectNode() {
    this.dom.classList.remove('ProseMirror-selectednode');
  }

  // update(node: Node): boolean {
  //   return false;
  // }
}

export {
  TaskSpec,
  TaskView,
};
